import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Shield, Sparkles, MapPin } from "lucide-react";
import { Hero } from "../components/Hero";
import { Marquee } from "../components/Marquee";
import { Reveal } from "../components/Reveal";
import { SectionHead } from "../components/SectionHead";
import { BriefForm } from "../components/BriefForm";
import { api, track } from "../lib/api";
import {
  CONTACT, EXPERIENCES, PILLARS, PROCESS, LOCATIONS, IMG,
  HOSPITALITY_ITEMS, SECURITY_ITEMS, TALENT_CATEGORIES,
} from "../data/content";

const Chapter = ({ num }) => (
  <span className="font-accent italic text-[#C9A55C]/70 text-sm tracking-[0.3em]">Chapter {num}</span>
);

const TransformationSlider = () => {
  const [pos, setPos] = useState(50);
  return (
    <div className="relative aspect-[16/9] overflow-hidden border border-[#C9A55C]/20 select-none" data-testid="transformation-slider">
      <img src={IMG.dimStage} alt="Empty venue before production" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <img src={IMG.stagePurple} alt="Venue transformed with stage, lighting and production" className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="w-px h-full bg-[#C9A55C]" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[#C9A55C] text-[#070708] flex items-center justify-center text-xs font-bold">↔</div>
      </div>
      <span className="absolute top-4 left-4 text-[0.6rem] uppercase tracking-[0.3em] bg-[#070708]/70 px-3 py-1.5 text-[#A9ABB2]">Before</span>
      <span className="absolute top-4 right-4 text-[0.6rem] uppercase tracking-[0.3em] bg-[#070708]/70 px-3 py-1.5 text-[#C9A55C]">After</span>
      <input
        type="range" min="0" max="100" value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
        aria-label="Before after comparison slider"
        data-testid="transformation-range"
      />
    </div>
  );
};

export default function Home() {
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const prodRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: prodRef, offset: ["start end", "end start"] });
  const prodY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  useEffect(() => {
    api.get("/portfolio").then((r) => setPortfolio(r.data.slice(0, 4))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <main>
      <Hero />

      {/* 2 · Brand statement */}
      <section className="py-24 lg:py-36" data-testid="brand-statement-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-3">
            <Chapter num="I" />
            <p className="mt-3 text-xs uppercase tracking-[0.35em] text-[#C9A55C]">Mega Experiences</p>
          </Reveal>
          <div className="lg:col-span-9">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-5xl leading-tight tracking-tight text-[#F3EEE5] max-w-3xl">
                We Don’t Just Organise Events. We Create <em className="font-accent italic text-[#C9A55C]">Moments</em> People Talk About.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-7 text-[#A9ABB2] leading-relaxed max-w-2xl text-sm sm:text-base">
                From the first idea to the final spotlight, Mega Celebrity Events brings together celebrity coordination, creative production, guest hospitality, event security and on-ground execution to create experiences with impact.
              </p>
              <p className="mt-6 text-xs uppercase tracking-[0.3em] text-[#F3EEE5]/60">
                Concept · Talent · Production · Hospitality · Execution
              </p>
              <Link to="/about" className="btn-ghost mt-9 inline-flex" data-testid="discover-mega-btn">
                Discover Mega <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee />

      {/* 3 · Signature experiences */}
      <section className="py-24 lg:py-32" data-testid="experiences-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <SectionHead eyebrow="Chapter II · Signature Experiences" title="Choose the Experience You Want to Create" />
            <Reveal delay={0.1}>
              <Link to="/experiences" className="btn-ghost" data-testid="explore-experiences-btn">Explore All Experiences</Link>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#C9A55C]/12 border border-[#C9A55C]/12">
            {EXPERIENCES.map((x, i) => (
              <Reveal key={x.slug} delay={(i % 4) * 0.08}>
                <Link
                  to={`/experiences/${x.slug}`}
                  onClick={() => track("service_view", { service: x.slug })}
                  className="group block bg-[#0D1018] hover:bg-[#11141f] transition-colors duration-500 h-full"
                  data-testid={`experience-card-${x.slug}`}
                >
                  <div className="img-frame aspect-[4/3]">
                    <img src={x.image} alt={x.title} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0D1018] via-transparent to-transparent" />
                  </div>
                  <div className="p-6">
                    <p className="text-[0.6rem] tracking-[0.3em] text-[#C9A55C]/70 uppercase mb-2">{String(i + 1).padStart(2, "0")}</p>
                    <h3 className="font-display text-xl text-[#F3EEE5] group-hover:text-[#C9A55C] transition-colors duration-300">{x.title}</h3>
                    <p className="mt-2 font-accent italic text-sm text-[#A9ABB2]">{x.tagline}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4 · Celebrity & talent */}
      <section className="py-24 lg:py-32 bg-[#0D1018] border-y border-[#C9A55C]/10 grain relative" data-testid="talent-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHead
              eyebrow="Chapter III · The Right Presence Changes the Room"
              title="Celebrity & Talent Coordination"
              description="From selecting the right personality to coordinating schedules, event requirements, hospitality and appearance flow, every engagement is approached professionally and confidentially."
            />
            <Reveal delay={0.15}>
              <div className="flex flex-wrap gap-2 mt-8">
                {TALENT_CATEGORIES.map((c) => (
                  <span key={c} className="text-xs tracking-wide border border-[#C9A55C]/25 text-[#F3EEE5]/80 px-4 py-2 rounded-full hover:border-[#C9A55C]/70 hover:text-[#C9A55C] transition-colors duration-300">
                    {c}
                  </span>
                ))}
              </div>
              <Link to="/celebrity-talent" className="btn-gold mt-10 inline-flex" data-testid="enquire-talent-btn">
                Enquire About Talent
              </Link>
              <p className="mt-6 text-xs text-[#A9ABB2]/70 max-w-md leading-relaxed">
                Talent availability, commercial terms and participation are subject to confirmation, contracts and applicable permissions.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="relative">
            <div className="img-frame border border-[#C9A55C]/20 p-3">
              <img src={IMG.dinnerEvent} alt="Elegant celebrity-style evening event" className="w-full aspect-[4/5] object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-[#070708] border border-[#C9A55C]/30 px-6 py-4">
              <p className="font-accent italic text-lg text-[#C9A55C]">Designed to be seen.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 5 · Production */}
      <section ref={prodRef} className="relative py-24 lg:py-36 overflow-hidden" data-testid="production-section">
        <motion.div className="absolute inset-0" style={{ y: prodY }}>
          <img src={IMG.production} alt="" className="w-full h-[120%] object-cover opacity-20" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#070708] via-transparent to-[#070708]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
          <SectionHead
            eyebrow="Chapter IV · Behind Every Great Moment"
            title="Production That Builds Anticipation"
            description="A powerful event is built through hundreds of details working together. Our production approach brings the stage, sound, lighting, visuals, timing and people into one coordinated experience."
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-[#C9A55C]/12 border border-[#C9A55C]/12 mt-14">
            {["Stage Design", "Professional Sound", "Intelligent Lighting", "LED Walls", "Entry Concepts", "Backstage Areas", "Special Effects", "Event Control"].map((item, i) => (
              <Reveal key={item} delay={(i % 4) * 0.06}>
                <div className="bg-[#070708]/90 p-6 h-full flex flex-col justify-between gap-6 hover:bg-[#0D1018] transition-colors duration-500">
                  <Sparkles size={16} className="text-[#C9A55C]/60" />
                  <p className="font-display text-lg text-[#F3EEE5]">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link to="/production" className="btn-ghost mt-10 inline-flex" data-testid="explore-production-btn">
              Explore Event Production <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 6 · Mega signature pillars */}
      <section className="py-24 lg:py-32 border-t border-[#C9A55C]/10" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <SectionHead eyebrow="Chapter V · The Mega Signature" title="What Makes an Event Feel Mega?" />
          <div className="mt-14 divide-y divide-[#C9A55C]/12 border-y border-[#C9A55C]/12">
            {PILLARS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.05}>
                <div className="group grid sm:grid-cols-12 gap-4 items-baseline py-7 px-2 hover:bg-[#0D1018] transition-colors duration-500" data-testid={`pillar-${p.num}`}>
                  <span className="sm:col-span-2 font-accent italic text-2xl text-[#C9A55C]/70">{p.num}</span>
                  <h3 className="sm:col-span-4 font-display text-xl sm:text-2xl text-[#F3EEE5] group-hover:text-[#C9A55C] transition-colors duration-300 uppercase tracking-wide">{p.title}</h3>
                  <p className="sm:col-span-6 text-sm text-[#A9ABB2] leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 7 · Transformation */}
      <section className="py-24 lg:py-32 bg-[#0D1018] border-y border-[#C9A55C]/10" data-testid="transformation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <SectionHead
            eyebrow="Chapter VI · Transformation"
            title="From an Empty Venue to an Unforgettable Experience"
            description="Drag the divider — stage construction, lighting, décor, rehearsal, guest arrival and the main performance, all working toward one final celebration."
          />
          <Reveal delay={0.15} className="mt-12">
            <TransformationSlider />
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/portfolio" className="btn-ghost mt-10 inline-flex" data-testid="see-transformations-btn">See Our Transformations</Link>
          </Reveal>
        </div>
      </section>

      {/* 8 · Hospitality */}
      <section className="py-24 lg:py-32" data-testid="hospitality-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="order-2 lg:order-1">
            <div className="img-frame border border-[#C9A55C]/20 p-3">
              <img src={IMG.dining} alt="Five-star event hospitality and fine dining" className="w-full aspect-[4/5] object-cover" loading="lazy" />
            </div>
          </Reveal>
          <div className="order-1 lg:order-2">
            <SectionHead
              eyebrow="Chapter VII · Hospitality & Protocol"
              title="Every Important Guest Should Feel Expected"
              description="Premium event hospitality begins before the guest reaches the venue. We support organisers with arrival planning, protocol coordination, reception, backstage hospitality and guest movement."
            />
            <Reveal delay={0.15}>
              <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3">
                {HOSPITALITY_ITEMS.map((h) => (
                  <li key={h} className="text-sm text-[#A9ABB2] flex items-center gap-2">
                    <span className="w-1 h-1 bg-[#C9A55C] rounded-full shrink-0" /> {h}
                  </li>
                ))}
              </ul>
              <Link to="/plan-your-event" className="btn-ghost mt-10 inline-flex" data-testid="plan-hospitality-btn">Plan Hospitality Support</Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 9 · Security */}
      <section className="py-24 lg:py-32 bg-[#3A0C19]/25 border-y border-[#C9A55C]/10" data-testid="security-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHead
              eyebrow="Chapter VIII · Confidence Behind the Experience"
              title="Premium Event Security by Celebrity Bouncers"
              description="For events requiring a disciplined and guest-conscious security presence, Mega Celebrity Events can integrate bouncers, VIP movement support, access management and restricted-area coordination into the overall event plan."
            />
            <Reveal delay={0.15}>
              <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3">
                {SECURITY_ITEMS.map((s) => (
                  <li key={s} className="text-sm text-[#A9ABB2] flex items-center gap-2">
                    <Shield size={12} className="text-[#C9A55C] shrink-0" /> {s}
                  </li>
                ))}
              </ul>
              <a
                href="https://CelebrityBouncers.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost mt-10 inline-flex"
                data-testid="explore-security-btn"
              >
                Explore Event Security <ArrowRight size={14} />
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="img-frame border border-[#C9A55C]/20 p-3">
              <img src={IMG.party} alt="Controlled, guest-conscious event entrance" className="w-full aspect-[4/5] object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 10 · Process */}
      <section className="py-24 lg:py-32" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <SectionHead eyebrow="Chapter IX · The Craft" title="From First Conversation to Final Applause" />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#C9A55C]/12 border border-[#C9A55C]/12">
            {PROCESS.map((s, i) => (
              <Reveal key={s.num} delay={(i % 3) * 0.08}>
                <div className="bg-[#0D1018] p-8 h-full hover:bg-[#11141f] transition-colors duration-500" data-testid={`process-step-${s.num}`}>
                  <p className="font-accent italic text-3xl text-[#C9A55C]/60">{s.num}</p>
                  <h3 className="font-display text-xl text-[#F3EEE5] mt-4 uppercase tracking-wide">{s.title}</h3>
                  <p className="mt-3 text-sm text-[#A9ABB2] leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link to="/plan-your-event" className="btn-gold mt-10 inline-flex" data-testid="start-conversation-btn">Start the Conversation</Link>
          </Reveal>
        </div>
      </section>

      {/* 11 · Portfolio preview */}
      <section className="py-24 lg:py-32 bg-[#0D1018] border-y border-[#C9A55C]/10" data-testid="portfolio-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <SectionHead eyebrow="Selected Experiences" title="Moments We Helped Bring to Life" />
            <Reveal delay={0.1}>
              <Link to="/portfolio" className="btn-ghost" data-testid="explore-portfolio-btn">Explore Our Portfolio</Link>
            </Reveal>
          </div>
          {portfolio.length === 0 ? (
            <p className="font-accent italic text-xl text-[#A9ABB2]">New experiences are being prepared for publication.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {portfolio.map((p, i) => (
                <Reveal key={p._id || i} delay={i * 0.07}>
                  <Link to="/portfolio" className="group block" data-testid={`portfolio-preview-${i}`}>
                    <div className="img-frame aspect-[3/4] border border-[#C9A55C]/15">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#070708] via-transparent to-transparent" />
                      <div className="absolute bottom-0 p-5">
                        <p className="text-[0.6rem] uppercase tracking-[0.3em] text-[#C9A55C]">{p.category}</p>
                        <h3 className="font-display text-lg text-[#F3EEE5] mt-1">{p.title}</h3>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 12 · Locations */}
      <section className="py-24 lg:py-32" data-testid="locations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <SectionHead
            eyebrow="Chapter X · The Region"
            title="Creating Experiences Across AP & Telangana"
            description="Based in Vijayawada, Mega Celebrity Events supports suitable events across Andhra Pradesh and Telangana through event-specific planning and coordination."
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#C9A55C]/12 border border-[#C9A55C]/12">
            {LOCATIONS.map((l, i) => (
              <Reveal key={l.slug} delay={(i % 3) * 0.07}>
                <Link
                  to={`/locations/${l.slug}`}
                  onClick={() => track("location_view", { city: l.slug })}
                  className="group bg-[#070708] p-8 block h-full hover:bg-[#0D1018] transition-colors duration-500"
                  data-testid={`location-card-${l.slug}`}
                >
                  <MapPin size={16} className="text-[#C9A55C]" />
                  <h3 className="font-display text-2xl text-[#F3EEE5] mt-4 group-hover:text-[#C9A55C] transition-colors duration-300">{l.name}</h3>
                  <p className="mt-2 text-sm text-[#A9ABB2] leading-relaxed">{l.note}</p>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-6 text-xs text-[#A9ABB2]/70">Other locations subject to requirements and availability.</p>
            <Link to="/plan-your-event" className="btn-ghost mt-6 inline-flex" data-testid="check-availability-btn">Check Availability in Your City</Link>
          </Reveal>
        </div>
      </section>

      {/* 13 · Testimonials (only when genuine ones exist) */}
      {testimonials.length > 0 && (
        <section className="py-24 lg:py-32 bg-[#0D1018] border-y border-[#C9A55C]/10" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <SectionHead eyebrow="Kind Words" title="After the Lights Come Down, the Experience Remains" />
            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <Reveal key={t._id || i} delay={i * 0.07}>
                  <blockquote className="border border-[#C9A55C]/15 bg-[#070708] p-8 h-full" data-testid={`testimonial-${i}`}>
                    <p className="font-accent italic text-lg text-[#F3EEE5]/90 leading-relaxed">“{t.quote}”</p>
                    <footer className="mt-6 text-xs uppercase tracking-[0.25em] text-[#C9A55C]">
                      {t.name} {t.role && <span className="text-[#A9ABB2] normal-case tracking-normal">· {t.role}</span>}
                    </footer>
                  </blockquote>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 14 · Plan your event */}
      <section className="py-24 lg:py-32" data-testid="plan-event-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <SectionHead
            eyebrow="Chapter XI · Begin"
            title="Tell Us What You Want People to Remember"
            description="Share a few details and our event team will review your vision and contact you to discuss the next stage."
            align="center"
          />
          <Reveal delay={0.15} className="mt-12">
            <BriefForm />
          </Reveal>
        </div>
      </section>

      {/* 15 · Final CTA */}
      <section className="relative py-32 lg:py-44 overflow-hidden grain" data-testid="final-cta-section">
        <div className="absolute inset-0">
          <img src={IMG.crowd} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#070708] via-[#070708]/60 to-[#070708]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl leading-tight tracking-tight text-[#F3EEE5]">
              Your Occasion Deserves More Than an Arrangement.
            </h2>
            <p className="mt-4 font-accent italic text-2xl sm:text-3xl text-[#C9A55C]">It Deserves a Moment.</p>
            <p className="mt-6 text-[#A9ABB2] text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
              Speak with Mega Celebrity Events about creating a distinctive celebrity, entertainment or luxury event experience.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <button onClick={() => { track("create_event_click", { location: "final_cta" }); navigate("/plan-your-event"); }} className="btn-gold" data-testid="final-create-event-btn">
                Create Your Event
              </button>
              <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { location: "final_cta" })} className="btn-ghost" data-testid="final-whatsapp-btn">
                WhatsApp Now
              </a>
              <a href={`tel:${CONTACT.phonePrimaryRaw}`} onClick={() => track("call_click", { location: "final_cta" })} className="btn-ghost" data-testid="final-call-btn">
                Call {CONTACT.phonePrimary}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
